import Vue from "vue";
import Vuex from "vuex";

import user from "./modules/user";
import licenses from "./modules/licenses";
import billing from "./modules/billing";
import cart from "./modules/cart";
import chat from "./modules/chat";
import contacts from "./modules/contacts";
import notifications from "./modules/notifications";

import API from "@/api";
import router from "@/router";
import productDetails from "@/data/products";
import deferred from "@/helpers/deferred";

Vue.use(Vuex);

const modules = {
  user,
  licenses,
  billing,
  cart,
  chat,
  contacts,
  notifications
};

const state = {
  adminSession: null,
  pendingRequests: 0,
  token: JSON.parse(localStorage.getItem("user-token")),
  products: [],
  request2FA: null,
  preventNav: false
};

export const getters = {
  isAdmin: state => {
    return state.adminSession !== null;
  },

  hasPermission: (state, getters) => permission => {
    if (state.adminSession) {
      return state.adminSession.user.permissions.includes(permission);
    }
    return getters["user/hasPermission"](permission);
  },

  displayedProducts(state) {
    const available = Object.values(productDetails.available);

    return state.products
      .filter(product => {
        return available.includes(product.id);
      })
      .sort((prev, next) => {
        return available.indexOf(prev.id) - available.indexOf(next.id);
      });
  }
};

export const mutations = {
  REQUEST_INCREMENT(state) {
    state.pendingRequests++;
  },

  REQUEST_DECREMENT(state) {
    state.pendingRequests--;
  },

  SET_2FA_REQUEST(state, request) {
    state.request2FA = request;
  },

  SET_PREVENT_NAV(state, prevent) {
    state.preventNav = prevent;
  },

  UPDATE_ADMIN(state, session) {
    state.adminSession = session;
  },

  UPDATE_PRODUCTS(state, data) {
    state.products = data;
  },

  UPDATE_TOKEN(state, token) {
    state.token = token;
    localStorage.setItem("user-token", JSON.stringify(token));
  }
};

export const actions = {
  async borrowSession({ commit, state }, data) {
    commit("REQUEST_INCREMENT");
    try {
      const { success, results } = await API.borrowSession(data);

      if (success) {
        const adminUser = JSON.parse(JSON.stringify(state.user));
        commit("UPDATE_ADMIN", { token: state.token, user: adminUser });

        const user = {
          ...results.user,
          token: results.token,
          license: results.license,
          organization: results.organization,
          permissions: results.permissions || [],
          roles: results.roles || []
        };
        commit("user/PATCH_USER", user);
        commit("chat/RESET");
        router.push("/");
      }
    } catch (e) {
      // handled by API
    } finally {
      commit("REQUEST_DECREMENT");
    }
  },

  restoreSession() {
    window.location.reload();
  },

  async getProducts({ commit }) {
    commit("REQUEST_INCREMENT");
    try {
      const { success, results } = await API.getProducts();

      if (success) {
        const products = results.list.map(product => {
          // Get the top level product identifier. i.e: 'plus' from 'nanome-plus-monthly'
          product.type =
            product.type ||
            product.id.replace(/(-.+)-\w+$/, "$1").replace("nanome-", "");

          if (product.bundle > 1) {
            product.type += "-bundle";
          }

          const details = productDetails[product.type];
          if (details) {
            product.details = details;
          } else {
            product.details = {
              name: product.type,
              features: []
            };
          }

          return product;
        });

        commit("UPDATE_PRODUCTS", products);
      }
    } catch (error) {
      // handled by API
    } finally {
      commit("REQUEST_DECREMENT");
    }
  },

  request2FA({ commit }) {
    const request = deferred();
    commit("SET_2FA_REQUEST", request);
    return request;
  }
};

const store = new Vuex.Store({
  modules,
  state,
  getters,
  mutations,
  actions
});

// Fires whenever store is updated
// store.subscribe((mutation, state) => {
//   console.log(mutation);
// });

export default store;
